// ─────────────────────────────────────────────────────────────────────────────
// LogsTab.js — the node's LIVE log tail, streamed over SSE.
//
// The modal mounts only the active tab, so opening this tab opens the stream and
// leaving it (or closing the modal) tears it down — we never hold a socket per
// table row. useNodeLogs owns the connection, the backoff and the ring buffer.
//
// Every connection state is spelled out rather than falling through to an empty
// console: "connecting", "nothing has arrived yet" and "this node has no stream"
// are three different things, and an admin debugging a node needs to know which
// one they are looking at. The stream is genuinely unavailable for any offline
// node — the backend proxies to the node itself — so that state is normal here,
// not a failure.
//
// Each line is `[time] [LEVEL] text`; the level chip is coloured (SUCCESS green,
// WARN amber, ERROR red, INFO muted).
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef } from "react";
import styles from "../NodeDetailModal.module.css";
import { useNodeLogs } from "../../../hooks/useNodeLogs";

const LEVEL_CLASS = {
  SUCCESS: styles.lvlSuccess,
  WARN:    styles.lvlWarn,
  ERROR:   styles.lvlError,
  INFO:    styles.lvlInfo,
};

const EMPTY_MESSAGE = {
  connecting:  "Connecting to the log stream…",
  open:        "Connected — waiting for output…",
  unavailable: "Live logs aren't available for this node. It has to be online and reachable to stream.",
  idle:        "No log stream.",
};

/** Backend frames carry an ISO timestamp; fall back to the raw value. */
function fmtTime(ts) {
  if (!ts) return "";
  const at = new Date(ts);
  return Number.isNaN(at.getTime()) ? String(ts) : at.toLocaleTimeString();
}

export default function LogsTab({ nodeId }) {
  const { lines, status } = useNodeLogs(nodeId);
  const endRef = useRef(null);

  // Follow the tail as lines arrive.
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [lines.length]);

  return (
    <div className={styles.tab}>
      <div className={styles.console}>
        {lines.map((line, i) => (
          <div key={`${line.timestamp}-${i}`} className={styles.logLine}>
            <span className={styles.logTime}>[{fmtTime(line.timestamp)}]</span>
            <span className={`${styles.logLevel} ${LEVEL_CLASS[line.level] || styles.lvlInfo}`}>
              [{line.level || "INFO"}]
            </span>
            <span className={styles.logText}>{line.short || line.message}</span>
          </div>
        ))}

        {lines.length === 0 && (
          <div className={styles.logEmpty}>{EMPTY_MESSAGE[status] ?? EMPTY_MESSAGE.idle}</div>
        )}

        <div ref={endRef} />
      </div>
    </div>
  );
}
